import React, { useId, useState } from "react";

import {
  GroupScores,
  ResilienceReport,
  ScoreTile,
} from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import {
  K,
  LimitedDataset,
  Section,
  useDomainName,
  useRichText,
  useSegmentName,
} from "./parts";
import {
  band,
  DIMENSIONS,
  Dimension,
  DOMAINS,
  Domain,
  formatGroupScore,
} from "./resilience-view";
import ScoreRadar, {
  MINIMUM_DOMAINS,
  radarDomains,
  RadarSeries,
} from "./ScoreRadar";
import { useDashboardTracking } from "../tracking";
import styles from "./resilience.module.scss";

type Segment = { dimension: Dimension; name: string };

// The two picks sit in fixed slots, orange then grey, so a pick keeps its
// colour while the other changes. `older` is the slot a third tick replaces.
type Picks = {
  slots: [Segment | null, Segment | null];
  older: 0 | 1;
};

const same = (a: Segment | null, b: Segment) =>
  a !== null && a.dimension === b.dimension && a.name === b.name;

const toggled = ({ slots, older }: Picks, segment: Segment): Picks => {
  const at = slots.findIndex((one) => same(one, segment));
  if (at !== -1) {
    const next: Picks["slots"] = [...slots];
    next[at] = null;
    return { slots: next, older: at === 0 ? 1 : 0 };
  }
  const empty = slots.indexOf(null);
  if (empty !== -1) {
    const next: Picks["slots"] = [...slots];
    next[empty] = segment;
    const other = empty === 0 ? 1 : 0;
    return {
      slots: next,
      older: next[other] === null ? (empty as 0 | 1) : other,
    };
  }
  const next: Picks["slots"] = [...slots];
  next[older] = segment;
  return { slots: next, older: older === 0 ? 1 : 0 };
};

// Platform never sends a group too small to show, but a group it sends can
// still have any score hidden, its Index included. One with nothing shown
// has nothing to compare.
const pickable = (group: GroupScores) =>
  !group.index.suppressed ||
  DOMAINS.some((domain) => !group.domains[domain].suppressed);

const domainValues = (group: GroupScores) => {
  const values: Partial<Record<Domain, number>> = {};
  DOMAINS.forEach((domain) => {
    const tile = group.domains[domain];
    if (!tile.suppressed) values[domain] = tile.value;
  });
  return values;
};

const Versus = ({ first, second }: { first: ScoreTile; second: ScoreTile }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const rich = useRichText();
  const figure = (tile: ScoreTile) =>
    tile.suppressed
      ? t(`${K}.cohorts.hiddenScore`)
      : formatGroupScore(tile.value, i18n.language);
  return (
    <>
      {rich(
        `${K}.cohorts.versus`,
        {},
        {
          first: (
            <span className={styles["figure-first"]}>{figure(first)}</span>
          ),
          second: (
            <span className={styles["figure-second"]}>{figure(second)}</span>
          ),
        }
      )}
    </>
  );
};

const Findings = ({
  names,
  groups,
}: {
  names: [string, string];
  groups: [GroupScores, GroupScores];
}) => {
  const { t, i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const rich = useRichText();
  const bandName = (score: number) => t(`${K}.bands.${band(score)}`);
  const strong = (text: string) => <strong>{text}</strong>;

  // Which of the two scores higher on the Index, when both Indexes show.
  const ranking = (scoreA: number, scoreB: number) => {
    if (scoreA === scoreB) {
      return rich(
        `${K}.cohorts.findingsLevel`,
        {
          score: formatGroupScore(scoreA, i18n.language),
          band: bandName(scoreA),
        },
        { first: strong(names[0]), second: strong(names[1]) }
      );
    }
    const [high, low] =
      scoreA > scoreB
        ? [
            { name: names[0], score: scoreA },
            { name: names[1], score: scoreB },
          ]
        : [
            { name: names[1], score: scoreB },
            { name: names[0], score: scoreA },
          ];
    return rich(
      `${K}.cohorts.findings`,
      {
        highScore: formatGroupScore(high.score, i18n.language),
        highBand: bandName(high.score),
        lowScore: formatGroupScore(low.score, i18n.language),
        lowBand: bandName(low.score),
      },
      { high: strong(high.name), low: strong(low.name) }
    );
  };
  const [a, b] = groups;

  // Ties go to the domain listed first.
  let widest: { domain: Domain; points: number } | null = null;
  let sharedDomains = 0;
  for (const domain of DOMAINS) {
    const x = a.domains[domain];
    const y = b.domains[domain];
    if (x.suppressed || y.suppressed) continue;
    sharedDomains += 1;
    const points = Math.abs(x.value - y.value);
    if (points > 0 && (widest === null || points > widest.points)) {
      widest = { domain, points };
    }
  }

  return (
    <div className={styles["findings"]} aria-live="polite">
      <p className={styles["findings-eyebrow"]}>
        {t(`${K}.cohorts.findingsLabel`)}
      </p>
      <p className={styles["findings-text"]}>
        {!a.index.suppressed && !b.index.suppressed && (
          <>{ranking(a.index.value, b.index.value)} </>
        )}
        {sharedDomains === 0
          ? t(`${K}.cohorts.noSharedDomain`)
          : widest === null
          ? t(`${K}.cohorts.noGap`)
          : rich(
              `${K}.cohorts.gap`,
              {
                count: widest.points,
                points: formatGroupScore(widest.points, i18n.language),
              },
              {
                domain: strong(domainName(widest.domain)),
                pair: (
                  <Versus
                    first={a.domains[widest.domain]}
                    second={b.domains[widest.domain]}
                  />
                ),
              }
            )}
      </p>
    </div>
  );
};

const useCohortNames = () => {
  const { t } = useTranslation(["admin"]);
  const segmentName = useSegmentName();
  return (first: Segment, second: Segment) => {
    // A location and a department can share a name; say which is which.
    const plain = [
      segmentName(first.dimension, first.name),
      segmentName(second.dimension, second.name),
    ];
    return [first, second].map((segment, index) =>
      plain[0] === plain[1]
        ? t(`${K}.cohorts.qualified`, {
            name: plain[index],
            dimension: t(`${K}.cohorts.dimensions.${segment.dimension}`),
          })
        : plain[index]
    ) as [string, string];
  };
};

const Comparison = ({
  first,
  second,
  names,
  report,
}: {
  first: Segment;
  second: Segment;
  names: [string, string];
  report: ResilienceReport;
}) => {
  const { t } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const segments = report.scores.selected.segments;
  const a = segments[first.dimension][first.name];
  const b = segments[second.dimension][second.name];

  const series: RadarSeries[] = [
    { name: names[0], values: domainValues(a), tone: "accent" },
    { name: names[1], values: domainValues(b), tone: "muted" },
  ];
  const hasRadar = radarDomains(series).length >= MINIMUM_DOMAINS;

  return (
    <>
      <div className={styles["compare"]} data-has-radar={hasRadar}>
        {hasRadar && (
          <div className={styles["compare-radar"]}>
            <ScoreRadar series={series} format={formatGroupScore} />
            <ul
              className={styles["compare-key"]}
              aria-label={t(`${K}.cohorts.keyLabel`)}
            >
              {names.map((name, index) => (
                <li
                  key={index}
                  className={styles["compare-key-item"]}
                  data-slot={index === 0 ? "first" : "second"}
                >
                  {name}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ul
          className={styles["compare-cards"]}
          aria-label={t(`${K}.cohorts.cardsLabel`)}
        >
          {DOMAINS.map((domain) => (
            <li key={domain} className={styles["compare-card"]}>
              <span className={styles["compare-domain"]}>
                {domainName(domain)}
              </span>
              <span className={styles["compare-figures"]}>
                <Versus first={a.domains[domain]} second={b.domains[domain]} />
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Findings names={names} groups={[a, b]} />
    </>
  );
};

const Cohorts = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const segmentName = useSegmentName();
  const cohortNames = useCohortNames();
  const idPrefix = useId();
  const { filterChanged } = useDashboardTracking();
  const segments = report.scores.selected.segments;

  const columns = DIMENSIONS.map((dimension) => ({
    dimension,
    names: Object.keys(segments[dimension]).sort((a, b) =>
      segmentName(dimension, a).localeCompare(
        segmentName(dimension, b),
        i18n.language
      )
    ),
  })).filter((column) => column.names.length > 0);

  const offered = columns.map(({ dimension, names }) =>
    names
      .filter((name) => pickable(segments[dimension][name]))
      .map((name) => ({ dimension, name }))
  );
  const choices: Segment[] = offered.flat();
  // A column with a real choice opens the comparison; with none, every column
  // is a single group and the client can only widen it by sharing more.
  const roomy = offered.find((column) => column.length >= 2);

  const [picks, setPicks] = useState<Picks>(() => {
    const opening = roomy ?? choices;
    return { slots: [opening[0] ?? null, opening[1] ?? null], older: 0 };
  });

  // Nothing to compare: leave the section out, as the prototype does.
  if (choices.length < 2) return null;

  const [first, second] = picks.slots;
  const names =
    first !== null && second !== null ? cohortNames(first, second) : null;

  return (
    <Section
      title={t(`${K}.sections.cohorts`)}
      info={t(`${K}.cohorts.info`)}
      eyebrow={
        names
          ? t(`${K}.cohorts.versus`, {
              first: names[0],
              second: names[1],
            })
          : undefined
      }
    >
      {roomy ? (
        <p className={styles["card-description"]}>
          {t(`${K}.cohorts.description`)}
        </p>
      ) : (
        <LimitedDataset
          section={t(`${K}.sections.cohorts`)}
          message={t(`${K}.cohorts.limited`)}
        />
      )}
      <div className={styles["pickers"]}>
        {columns.map(({ dimension, names }) => (
          <fieldset key={dimension} className={styles["picker"]}>
            <legend className={styles["picker-legend"]}>
              {t(`${K}.cohorts.dimensions.${dimension}`)}
            </legend>
            {names.map((name, index) => {
              const segment = { dimension, name };
              const enabled = pickable(segments[dimension][name]);
              const slot = picks.slots.findIndex((one) => same(one, segment));
              const id = `${idPrefix}-${dimension}-${index}`;
              return (
                <div
                  key={name}
                  className={styles["picker-option"]}
                  data-slot={
                    slot === 0 ? "first" : slot === 1 ? "second" : undefined
                  }
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={slot !== -1}
                    disabled={!enabled}
                    onChange={() => {
                      filterChanged("resilience", "cohort");
                      setPicks((was) => toggled(was, segment));
                    }}
                  />
                  <label htmlFor={id}>
                    {segmentName(dimension, name)}
                    {!enabled && (
                      <span className={styles["picker-hidden"]}>
                        {" "}
                        {t(`${K}.notEnoughData`)}
                      </span>
                    )}
                  </label>
                </div>
              );
            })}
          </fieldset>
        ))}
      </div>
      {first !== null && second !== null && names !== null ? (
        <Comparison
          first={first}
          second={second}
          names={names}
          report={report}
        />
      ) : (
        <p className={styles["not-enough"]}>{t(`${K}.cohorts.pickTwo`)}</p>
      )}
    </Section>
  );
};

export default Cohorts;
